"use client";

import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
  CartSideSheet,
  type CartSideSheetProps
} from "../../molecules/cart-side-sheet/cart-side-sheet";
import type { CartCmsSource } from "../../molecules/cart-side-sheet/use-cart-cms";
import styles from "./nav-cart.module.css";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * The seam between GlobalNav's cart button and the CartSideSheet. Renders a
 * hidden popover shell under `panelId` so the browser's `popovertarget`
 * mechanism has a target, but INTERCEPTS the open in `beforetoggle` and
 * drives the sheet from state instead. It owns the one `<dialog>`-based
 * CartSideSheet and its `showModal()` — the modal + `::backdrop` that
 * `popovertarget` alone cannot supply. A lazy, headless
 * `<CartSideSheetContainer />` reads localStorage, fetches Storyblok, runs
 * the adapter and reports that sheet's props back.
 *
 * ⚠ The shell exists ONLY to satisfy the browser's popovertarget target; it
 * never actually opens. `beforetoggle`'s `preventDefault()` cancels the
 * platform open and React state takes over.
 *
 * ⚠ IT MUST RENDER INSIDE `[data-dds-root]`, same rule as `nav-search`. The
 * shell doesn't need tokens itself but the CartSideSheet inside does.
 */

/* Lazy — container + adapter + CMS hook ship as one chunk, requested when the
   shopper first points at the cart and needed only when it opens. It renders
   nothing; it reports the sheet's props back through `onProps`. */
const loadContainer = () => import("../../molecules/cart-side-sheet/cart-side-sheet-container");
const CartSideSheetContainer = React.lazy(loadContainer);

/* Guarded — jsdom ships no Popover API (`showPopover`, `hidePopover`,
   `:popover-open` all undefined). Not a browser-support guard. */
const dismissStub = (host: HTMLElement | null) => {
  const hostWithPopover = host as (HTMLElement & { hidePopover?: () => void }) | null;
  if (typeof hostWithPopover?.hidePopover !== "function") {
    return;
  }
  try {
    hostWithPopover.hidePopover();
  } catch {
    /* Already hidden — the platform throws InvalidStateError on hidePopover
       of a closed popover; we don't care which state it was in. */
  }
};

export interface NavCartProps {
  /**
   * ⚠ Must be the same string handed to GlobalNav as `cart.panelId` — the
   * button carries `popovertarget={panelId}` and this seam registers the
   * shell under `id={panelId}`.
   */
  panelId: string;
  /** The on-screen eyebrow (and the dialog's accessible name), or `ariaLabel`
   *  for a name with no eyebrow — supply exactly one, the rule the underlying
   *  `CartSideSheet` enforces via its label union. An eyebrow is replaced by the
   *  CMS panel title once it loads; this string is the fallback. */
  eyebrowLabel?: string;
  ariaLabel?: string;
  /** Storyblok token, cached-version, and fetcher used to hydrate cart copy.
   *  Passed straight down to the lazy container — DDS never reaches for a
   *  cross-boundary import of its own. When omitted, the sheet falls back
   *  to the adapter's DEFAULTS (fine for a demo, not for production). */
  cartCms?: CartCmsSource;
}

function NavCartImpl({ panelId, eyebrowLabel, ariaLabel, cartCms }: NavCartProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [reported, setReported] = useState<CartSideSheetProps | null>(null);

  /* Intercept the platform's popovertarget open. Two things matter:
     1. `beforetoggle` fires on both directions — only hijack "open".
     2. On mount, the DOM may ALREADY be open (a fast click landed before
        the chunk hydrated → UA opened the stub while nothing was
        listening). Adopt that state so the user's first click isn't
        wasted — nav-search.client.tsx solves the same way at :170-175. */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) {
      return undefined;
    }
    const onBeforeToggle = (event: Event) => {
      const toggle = event as ToggleEvent;
      if (toggle.newState !== "open") {
        return;
      }
      /* Cancel the platform popover open — our state drives the sheet
         instead, so the dialog's showModal() owns the top layer without a
         popover shell competing for it. */
      event.preventDefault();
      /* If the UA opened the stub before we could preventDefault
         (pre-hydration click), close it as we take over. */
      dismissStub(host);
      setOpen(true);
    };
    host.addEventListener("beforetoggle", onBeforeToggle);

    /* Adopt the DOM's current state. `popovertarget` in server HTML opens
       the shell with no JavaScript, and no `toggle` fires for an open that
       already happened. Without this, click 1 → nothing, click 2 →
       nothing, click 3 → sheet. */
    try {
      if (host.matches(":popover-open")) {
        dismissStub(host);
        setOpen(true);
      }
    } catch {
      /* jsdom / older UAs throw on the pseudo-class selector — no adoption
         needed there, the effect still handles subsequent toggles. */
    }

    return () => host.removeEventListener("beforetoggle", onBeforeToggle);
  }, []);

  /* Warm on intent. The first hover or focus on the cart button requests the
     container chunk and calls `cartCms.warm`, so the first open does not wait
     on them in series — and a visit that never points at the cart pays for
     neither. */
  const warm = cartCms?.warm;
  useEffect(() => {
    const triggers = Array.from(document.querySelectorAll<HTMLElement>("[popovertarget]"))
      .filter(trigger => trigger.getAttribute("popovertarget") === panelId);
    let warmed = false;
    const onIntent = () => {
      if (warmed) {
        return;
      }
      warmed = true;
      /* A failed preload is not an error: React.lazy requests it again on open. */
      loadContainer().catch(() => undefined);
      warm?.();
    };
    triggers.forEach(trigger => {
      trigger.addEventListener("pointerenter", onIntent);
      trigger.addEventListener("focus", onIntent);
    });
    return () => triggers.forEach(trigger => {
      trigger.removeEventListener("pointerenter", onIntent);
      trigger.removeEventListener("focus", onIntent);
    });
  }, [panelId, warm]);

  /* Container reports every close route through here — remove-all confirm,
     close ✕, Escape, primary/secondary anchor navigation. Unmount clears
     the container, dropping the lazy chunk's runtime state; the CMS module
     cache stays warm for the next open.
     ⚠ Also dismiss the stub — if the UA reopened it via a stray click
     while the sheet was on screen, we don't want it lingering as a
     top-layer invisible box. */
  const handleCloseComplete = useCallback(() => {
    dismissStub(hostRef.current);
    setOpen(false);
    /* ⚠ Clear, or the next open paints the PREVIOUS cart for a frame: this
       seam does not unmount, so the reported props outlive the sheet. */
    setReported(null);
  }, []);

  /* ⚠ Route the seam's labels to the panel's discriminated union WITHOUT
     mixing them: an `eyebrowLabel` prints on screen; an `ariaLabel`
     doesn't. Feeding one into the other slot means the reader sees the
     accessible-name string as the sheet heading (or the eyebrow
     disappears). Same union reaches the real container below, not just
     the fallback. */
  const containerLabelProps: { eyebrowLabel: string; ariaLabel?: undefined }
    | { eyebrowLabel?: undefined; ariaLabel: string } = eyebrowLabel !== undefined
      ? { eyebrowLabel }
      : { ariaLabel: ariaLabel ?? "Cart summary" };

  /* What the sheet wears until the lazy chunk reports. It ships in this
     file's bundle so the skeleton paints instantly, and `onClose` is the
     seam's own closer because the container's router has not arrived yet —
     Escape during the fetch still closes. */
  const loadingProps: CartSideSheetProps = {
    ...containerLabelProps,
    id: `${panelId}-panel`,
    loading: true,
    onClose: handleCloseComplete,
    open: true
  };

  return (
    <>
      {/* The stub popover satisfies GlobalNav's requirement that "the app
          must render a popover element under panelId". popover="manual"
          means no light-dismiss, but popovertarget can still toggle it —
          which is exactly what beforetoggle above cancels. */}
      <div
        className={styles.host}
        id={panelId}
        popover="manual"
        ref={hostRef}
      />
      {open ? (
        <>
          {/* ⚠ ONE SHEET, AND THIS SEAM OWNS IT. The sheet sits OUTSIDE the
              Suspense subtree below, so the chunk arriving changes its props
              and never unmounts the `<dialog>`. A sheet inside the boundary is
              torn down and rebuilt instead — two showModal() calls, which the
              reader sees as a second slide-in.
              ⚠ `onClose` comes from `reported` once it lands: that is the
              container's router for the confirm view, remove-all and Escape.
              The loading props carry the seam's plain closer instead. */}
          <CartSideSheet {...(reported ?? loadingProps)} />
          <Suspense fallback={null}>
            <CartSideSheetContainer
              {...containerLabelProps}
              cartCms={cartCms}
              id={`${panelId}-panel`}
              onCloseComplete={handleCloseComplete}
              onProps={setReported}
            />
          </Suspense>
        </>
      ) : null}
    </>
  );
}
export const NavCart = withSafeRender(NavCartImpl, "NavCart");

export default NavCart;
